import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiHome, FiPhone, FiMail } = FiIcons;

const Success = () => {
  return (
    <div className="pt-16 min-h-screen bg-gradient-to-br from-primary-50 to-warm-100">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-8"
            >
              <div className="bg-white p-4 rounded-full shadow-lg">
                <div className="bg-primary-600 w-24 h-24 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-white" />
                </div>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-warm-900 mb-6"
            >
              Richiesta Inviata con Successo!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-warm-700 mb-12 max-w-3xl mx-auto"
            >
              Hai candidato il tuo immobile con successo!<br />
              Un nostro esperto sarà da te al più presto!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto"
            >
              <h2 className="text-2xl font-bold text-warm-900 mb-4">Cosa aspettarti ora</h2>
              <div className="space-y-4 text-left">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-2 rounded-full">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-warm-900">Conferma Ricevuta</h3>
                    <p className="text-warm-600 text-sm">Abbiamo ricevuto tutti i dati del tuo immobile</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-2 rounded-full">
                    <SafeIcon icon={FiPhone} className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-warm-900">Contatto Veloce</h3>
                    <p className="text-warm-600 text-sm">Un nostro consulente ti contatterà entro 24 ore</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-2 rounded-full">
                    <SafeIcon icon={FiHome} className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-warm-900">Valutazione Gratuita</h3>
                    <p className="text-warm-600 text-sm">Riceverai una valutazione completa del potenziale del tuo immobile</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/"
                className="inline-flex items-center justify-center bg-primary-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                <SafeIcon icon={FiHome} className="mr-2 h-5 w-5" />
                Torna alla Home
              </Link>
              <a
                href="mailto:elviraviglianopm@gmail.com"
                className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-warm-50 transition-colors duration-200 shadow-lg hover:shadow-xl border border-primary-200"
              >
                <SafeIcon icon={FiMail} className="mr-2 h-5 w-5" />
                Contattaci Ora
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-warm-900 mb-4">Hai altre domande?</h2>
            <p className="text-warm-600 max-w-2xl mx-auto">
              Consulta le nostre FAQ o contattaci direttamente per ulteriori informazioni
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-warm-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-warm-900 mb-3">Domande Frequenti</h3>
              <p className="text-warm-600 mb-4">
                Trova risposte immediate alle domande più comuni sui nostri servizi
              </p>
              <Link
                to="/faq"
                className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
              >
                Consulta le FAQ →
              </Link>
            </div>
            <div className="bg-warm-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-warm-900 mb-3">Contatti Diretti</h3>
              <p className="text-warm-600 mb-4">
                Per urgenze o richieste specifiche, contattaci direttamente
              </p>
              <Link
                to="/contatti"
                className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
              >
                Vai ai contatti →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Success;